import React, { useCallback, useEffect, useRef, useState } from "react";
import "../Css/asciiPortrait.css";

// Pool of characters cells flicker through while under the cursor.
const GLITCH_CHARS = "!<>-_\\/[]{}=+*^?#$&%01";

function pickGlitchChar() {
  return GLITCH_CHARS[Math.floor(Math.random() * GLITCH_CHARS.length)];
}

// Shows `src` as a plain photo, with a grid of characters overlaid on top —
// invisible until the pointer moves near them, at which point they flicker
// through random glyphs and fade in, settling back to invisible the moment
// the cursor moves away. Purely a decorative hover effect layered over the
// real image, not a conversion of the photo itself.
export default function AsciiPortrait({ src, alt = "", cols = 40, rows = 50, className = "" }) {
  const containerRef = useRef(null);
  const cellRefs = useRef([]);
  const activeStateRef = useRef([]); // parallel array: 0 = hidden, 0..1 = fade progress
  const rafRef = useRef(null);
  const pointerRef = useRef(null); // {x, y} in container-local px, or null when not hovering
  // Random phase/frequency for the hover shape's distortion — picked once
  // per mount so the wobble has a consistent (if irregular) identity.
  const noiseRef = useRef({
    a1: Math.random() * Math.PI * 2,
    f1: 3 + Math.floor(Math.random() * 3),
    a2: Math.random() * Math.PI * 2,
    f2: 6 + Math.floor(Math.random() * 4),
  });

  const [cellSize, setCellSize] = useState({ w: 10, h: 10 });

  useEffect(() => {
    cellRefs.current = new Array(cols * rows).fill(null);
    activeStateRef.current = new Array(cols * rows).fill(0);
  }, [cols, rows]);

  // Keep cell size responsive to the container's actual rendered size.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setCellSize({ w: width / cols, h: height / rows });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [cols, rows]);

  const onPointerMove = useCallback((evt) => {
    const rect = containerRef.current.getBoundingClientRect();
    pointerRef.current = {
      x: evt.clientX - rect.left,
      y: evt.clientY - rect.top,
    };
  }, []);

  const onPointerLeave = useCallback(() => {
    pointerRef.current = null;
  }, []);

  // Single rAF loop: cells within a radius of the pointer flicker through
  // random glyphs and ease toward fully visible; every other cell eases
  // back toward invisible. Writes directly to the DOM (not React state) —
  // this touches up to a couple thousand cells a frame, and a React
  // re-render per tick would be far too slow.
  useEffect(() => {
    const RADIUS = Math.max(cols, rows) * Math.max(cellSize.w, cellSize.h) * 0.18;
    const FADE_STEP = 0.18;
    // Fixed once per mount, not per frame — gives the wobble a stable
    // "shape" that slowly morphs over time instead of either a perfect
    // circle or frame-to-frame flicker noise.
    const { a1, f1, a2, f2 } = noiseRef.current;

    const tick = () => {
      const pointer = pointerRef.current;
      const state = activeStateRef.current;
      const refs = cellRefs.current;
      const t = performance.now() * 0.0006;

      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          const idx = row * cols + col;
          const el = refs[idx];
          if (!el) continue;

          let near = false;
          if (pointer) {
            const cx = (col + 0.5) * cellSize.w;
            const cy = (row + 0.5) * cellSize.h;
            const dx = cx - pointer.x;
            const dy = cy - pointer.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            const angle = Math.atan2(dy, dx);
            // Two sine harmonics at different frequencies/phases distort
            // the boundary into an irregular, organic blob instead of a
            // clean circle, and drift slowly over time.
            const wobble =
              1 +
              0.35 * Math.sin(angle * f1 + a1 + t) +
              0.2 * Math.sin(angle * f2 + a2 - t * 1.3);
            near = dist < RADIUS * wobble;
          }

          const prev = state[idx];
          const next = near
            ? Math.min(1, prev + FADE_STEP)
            : Math.max(0, prev - FADE_STEP);

          if (next !== prev) {
            el.style.opacity = next;
          }
          if (next > 0) {
            el.textContent = pickGlitchChar();
          }
          state[idx] = next;
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [cols, rows, cellSize]);

  const cellCount = cols * rows;

  return (
    <div
      className="ascii-portrait"
      ref={containerRef}
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
    >
      <img
        src={src}
        alt={alt}
        className={`ascii-portrait-img ${className}`}
      />
      <div
        className="ascii-portrait-overlay"
        aria-hidden="true"
        style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
      >
        {Array.from({ length: cellCount }).map((_, idx) => (
          <span
            key={idx}
            ref={(el) => {
              cellRefs.current[idx] = el;
            }}
            className="ascii-portrait-cell"
            style={{ opacity: 0 }}
          />
        ))}
      </div>
    </div>
  );
}
